// Resumen de facturación de un pedido.
//
// Va junto al bloque de cargas, encima de la subtabla de líneas. No sustituye a
// las bandas de factura de esa tabla: la banda agrupa las líneas y este bloque
// deja comparar las facturas entre sí sin recorrerla.
import { useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { PickingBar } from '@/components/common/PickingBar'
import { BotonMarca, useMarcadas } from '@/components/common/MarcasFilas'
import { claveFila } from '@/utils/marcasFilas'
import type { MarcasFilas, PedidoVentaLinea } from '@/types'
import { agruparPorFactura, totalFacturado } from '@/utils/facturasPedido'
import type { CoincideArticulo } from '@/utils/filtroArticulo'
import { SIN_MARCAS } from './marcasBloque'
import { fmtDate, fmtQty, fmtEur } from './formato'

interface Props {
  dark: boolean
  lineas: PedidoVentaLinea[]
  onVerAdjuntosFactura: (recid: string, factura: string) => void
  /** Búsqueda de artículo activa: las facturas que lo contienen arrancan
   *  desplegadas. El resaltado de la fila lo pone `renderLineas`. */
  coincide?: CoincideArticulo | null
  /** Pinta la tabla de líneas de una factura. La markup vive en LineasRow para
   *  que sea exactamente la misma que la de las líneas sin facturar.
   *  Opcional: con `sinDetalle` las filas no se despliegan y no hace falta. */
  renderLineas?: (filas: PedidoVentaLinea[]) => React.ReactNode
  /** Sin marco ni cabecera propios: la sección de fuera ya los aporta. */
  sinTarjeta?: boolean
  /** Sólo el resumen por factura, sin desplegable de líneas. Es lo que usa la
   *  pantalla dedicada: allí las líneas se analizan en su propia sección, a su
   *  grano, y repetirlas aquí sería contar dos veces lo mismo. */
  sinDetalle?: boolean
  /** Acción extra por factura, a la derecha. La pantalla dedicada la usa para
   *  saltar a la sección de líneas ya filtrada por esa factura. */
  accionFactura?: (factura: string) => React.ReactNode
  /** Marcas del usuario. Las pasan las dos pantallas que pintan el bloque —la
   *  lista de pedidos y la pestaña de Facturación del detalle—, cada una con su
   *  propio almacén, para que el recuento de cada una cuente sólo sus filas.
   *  Sigue siendo opcional: sin ella no hay canalón y el bloque se pinta
   *  exactamente igual que antes. */
  marcas?: MarcasFilas
  /** Clave de la fila del pedido; la de cada factura arranca por ella. */
  clavePedido?: string
}

export function FacturasPedido({
  dark, lineas, onVerAdjuntosFactura, coincide, renderLineas,
  sinTarjeta = false, sinDetalle = false, accionFactura, marcas, clavePedido,
}: Props) {
  const { t } = useTranslation()

  // Las facturas se pintan dentro de un `.map` y no son `<tr>`, así que no se
  // puede usar la pareja `FilaMarcable`/`MarcaCelda`, que se suscribe por fila.
  // Aquí se lee el conjunto entero: un pedido tiene una decena larga de facturas,
  // no miles, y marcar una repinta sólo este bloque. Sin marcas (pantalla de
  // detalle) el almacén inerte devuelve siempre el mismo conjunto vacío y esto no
  // repinta nunca.
  const marcadas = useMarcadas(marcas ?? SIN_MARCAS)
  const marcable = !!marcas && !!clavePedido
  // Las mismas clases que usa `FilaMarcable`, no un par de utilidades de
  // Tailwind: el `hover:bg-…` de la fila ganaría a la marca justo al pasar el
  // ratón por encima.
  const marcadoBg = dark ? 'fila-marcada-dark' : 'fila-marcada'

  const autoAbrir = useMemo(() => {
    const s = new Set<string>()
    if (!coincide) return s
    for (const l of lineas) {
      const f = l.factura?.trim()
      if (f && coincide(l.articulo, l.descripcion)) s.add(f)
    }
    return s
  }, [lineas, coincide])
  // Sólo lo que el usuario ha tocado. Lo que no está aquí sigue al despliegue
  // automático, así que si cambia la búsqueda las facturas intactas se
  // reorganizan solas y las que él abrió o cerró se quedan como las dejó.
  const [manual, setManual] = useState<Map<string, boolean>>(new Map())

  // Sin detalle, ninguna fila se despliega: no hay nada que abrir.
  const abierta = (factura: string) =>
    !sinDetalle && !!renderLineas && (manual.get(factura) ?? autoAbrir.has(factura))

  function toggle(factura: string) {
    const siguiente = !abierta(factura)
    setManual(prev => new Map(prev).set(factura, siguiente))
  }

  const facturas = agruparPorFactura(lineas)
  if (facturas.length === 0) return null

  const total = totalFacturado(lineas)

  const mutedTxt = dark ? 'text-slate-400' : 'text-slate-500'
  const mainTxt  = dark ? 'text-slate-100' : 'text-slate-800'
  const divider  = dark ? 'border-white/8' : 'border-slate-200'
  const cardBg   = dark ? 'bg-white/[0.03] border-white/8' : 'bg-slate-50/80 border-slate-200'

  return (
    <div className={sinTarjeta ? '' : `mb-3 rounded-lg border overflow-hidden ${cardBg}`}>
      {/* Cabecera: título + avance de facturación del pedido */}
      {!sinTarjeta && (
      <div className={`flex items-center gap-3 flex-wrap px-3 py-2 border-b ${divider}`}>
        <span className={`text-[10px] font-bold uppercase tracking-wider ${mutedTxt}`}>
          {t('salesOrders.billing.title')}
        </span>
        <span className={`text-[10px] ${mutedTxt}`}>
          {t('salesOrders.billing.count', { count: facturas.length })}
        </span>
        <div className="ml-auto flex items-center gap-2.5">
          <span className={`text-[10px] tabular-nums ${mutedTxt}`}>
            {fmtQty(total.facturada)} / {fmtQty(total.pedida)} {t('salesOrders.picking.units')}
          </span>
          <div className="w-[130px]">
            <PickingBar
              pct={total.pct}
              dark={dark}
              title={t('salesOrders.billing.totalTooltip', {
                facturada: fmtQty(total.facturada),
                pedida: fmtQty(total.pedida),
                importe: fmtEur(total.importe),
              })}
            />
          </div>
        </div>
      </div>
      )}

      {/* Una fila por factura */}
      <ul>
        {facturas.map((f, i) => {
          // Clave de negocio de la factura: el pedido más su número. Nunca el
          // índice, que cambia en cuanto se ordena o se filtra la lista.
          const clave = marcable ? claveFila(clavePedido, 'factura', f.factura) : ''
          const marcada = marcable && marcadas.has(clave)
          return (
          <li key={f.factura} className={`${i > 0 ? `border-t ${divider}` : ''} ${marcada ? marcadoBg : ''}`}>
          <div className="flex items-center gap-3 flex-wrap px-3 py-1.5">
            {marcable && marcas && (
              <BotonMarca marcas={marcas} clave={clave} dark={dark} marcada={marcada} />
            )}
            {sinDetalle || !renderLineas ? (
              // Sin desplegable no hay nada que pulsar: el número deja de ser
              // un botón en vez de fingir una interacción que no ocurre.
              <span className={`font-mono text-[11px] font-semibold ${mainTxt}`}>{f.factura}</span>
            ) : (
              <button
                type="button"
                onClick={() => toggle(f.factura)}
                aria-expanded={abierta(f.factura)}
                // Con la fila marcada el botón se queda sin hover propio: si no,
                // al pasar el ratón pintaría su fondo encima del tinte fucsia y
                // parecería que la marca se ha ido.
                className={`flex items-center gap-2 cursor-pointer text-left -my-1 py-1 pr-2 rounded transition-colors ${
                  marcada ? '' : (dark ? 'hover:bg-white/[0.06]' : 'hover:bg-white')
                }`}
              >
                <svg
                  className={`w-3 h-3 shrink-0 transition-transform duration-150 ${abierta(f.factura) ? 'rotate-90' : ''} ${mutedTxt}`}
                  viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                >
                  <path d="M5 11l4-4-4-4" />
                </svg>
                <span className={`font-mono text-[11px] font-semibold ${mainTxt}`}>{f.factura}</span>
              </button>
            )}
            {f.fecha && (
              <span className={`text-[10px] tabular-nums ${mutedTxt}`}>{fmtDate(f.fecha)}</span>
            )}
            <span className={`text-[10px] ${mutedTxt}`}>
              {t('salesOrders.invoice.lines', { count: f.lineas })}
            </span>

            <div className="ml-auto flex items-center gap-3">
              <span className={`text-[10px] tabular-nums ${mutedTxt}`}>
                {fmtQty(f.qtyFacturada)} {t('salesOrders.picking.units')}
              </span>
              <span className={`text-[11px] tabular-nums font-semibold ${mainTxt}`}>
                {fmtEur(f.importeFacturado)}
                {f.moneda && f.moneda !== 'EUR' && (
                  <span className={`ml-1 text-[9px] font-normal ${mutedTxt}`}>{f.moneda}</span>
                )}
              </span>
              {f.recid && (
                <button
                  type="button"
                  onClick={() => onVerAdjuntosFactura(f.recid!, f.factura)}
                  title={t('salesOrders.invoice.viewDocs')}
                  className={`flex items-center gap-1 text-[10px] px-2 py-1 rounded-lg border transition-colors cursor-pointer font-medium ${
                    dark
                      ? 'border-white/10 bg-white/5 text-slate-300 hover:bg-sky-500/15 hover:text-sky-300 hover:border-sky-500/30'
                      : 'border-slate-200 bg-white text-slate-600 hover:bg-sky-50 hover:text-sky-700 hover:border-sky-200 shadow-sm'
                  }`}
                >
                  <svg className="w-3.5 h-3.5" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M9 2H4a1 1 0 00-1 1v10a1 1 0 001 1h8a1 1 0 001-1V6L9 2z" />
                    <path d="M9 2v4h4M6 9h4M6 11.5h2.5" />
                  </svg>
                  <span>{t('salesOrders.invoice.docs')}</span>
                </button>
              )}
              {accionFactura?.(f.factura)}
            </div>
          </div>

          {abierta(f.factura) && renderLineas && (
            <div className={`px-3 pb-2 pl-8 overflow-x-auto border-t ${divider}`}>
              {renderLineas(f.filas)}
            </div>
          )}
          </li>
          )
        })}
      </ul>
    </div>
  )
}
